import React from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

export interface GridCol<T> {
  key: keyof T & string;
  label: string;
  width: number;
  /** select 면 options 중에서, list 면 자유 입력 + 제안 목록(datalist) */
  options?: Array<{ value: string; label: string }>;
  list?: string[];
  mono?: boolean;
}

export interface GridRow<T> { row: T; index: number }

interface Props<T> {
  id: string;
  cols: GridCol<T>[];
  rows: GridRow<T>[];
  onChange: (index: number, patch: Partial<T>) => void;
  onAdd: () => void;
  onRemove: (index: number) => void;
  /** 붙여넣기한 줄 수만큼 행이 모자라면 새로 만든다 — 만든 행의 index 를 돌려준다 */
  onAppend: (count: number) => number[];
  selected?: number | null;
  onSelect?: (index: number) => void;
  /** index → 'error' | 'warn' — 검증 결과를 행에 칠한다 */
  marks?: Map<number, 'error' | 'warn'>;
  /** 칸 오른쪽에 덧붙이는 읽기 전용 정보(타깃 컬럼의 자료형 등) */
  extra?: { label: string; width: number; render: (row: T) => React.ReactNode };
  readOnly?: boolean;
  empty?: React.ReactNode;
}

/**
 * 매핑 설계서의 편집 표 — 칸을 바로 고친다. 엑셀에서 여러 칸을 복사해 붙여 넣으면 그 자리부터 채운다
 * (탭·줄바꿈으로 나눈다). 수백 행까지는 그대로 그린다.
 */
function SpecGrid<T extends Record<string, any>>({ id, cols, rows, onChange, onAdd, onRemove, onAppend, selected, onSelect, marks, extra, readOnly, empty }: Props<T>) {
  const { t } = useTranslation();

  const paste = (e: React.ClipboardEvent<HTMLElement>, at: number, colIdx: number) => {
    const text = e.clipboardData.getData('text/plain');
    if (!text || (!text.includes('\t') && !text.includes('\n'))) return;   // 한 칸짜리는 보통 붙여넣기
    e.preventDefault();
    const lines = text.replace(/\r\n?/g, '\n').replace(/\n$/, '').split('\n').map((l) => l.split('\t'));
    const targets = rows.slice(at).map((r) => r.index);
    if (lines.length > targets.length) targets.push(...onAppend(lines.length - targets.length));
    lines.forEach((cells, i) => {
      const p: Record<string, string> = {};
      cells.forEach((v, j) => { const c = cols[colIdx + j]; if (c) p[c.key] = v.trim(); });
      onChange(targets[i], p as Partial<T>);
    });
  };

  return (
    <div className="h-full flex flex-col min-h-0">
      <div className="flex-1 min-h-0 overflow-auto border border-gray-200 rounded">
        <table className="text-xs border-collapse" style={{ minWidth: '100%' }}>
          <thead className="sticky top-0 bg-gray-100 z-10">
            <tr>
              <th className="w-8 px-1 py-1 text-gray-400 font-normal border-b border-gray-200">#</th>
              {cols.map((c) => (
                <th key={c.key} style={{ minWidth: c.width }} className="px-1 py-1 text-left font-semibold text-gray-600 border-b border-gray-200 whitespace-nowrap">{c.label}</th>
              ))}
              {extra && <th style={{ minWidth: extra.width }} className="px-1 py-1 text-left font-semibold text-gray-400 border-b border-gray-200 whitespace-nowrap">{extra.label}</th>}
              <th className="w-8 border-b border-gray-200" />
            </tr>
          </thead>
          <tbody>
            {rows.map(({ row, index }, ri) => {
              const mark = marks?.get(index);
              return (
                <tr key={index} data-row={`${id}-${index}`} onClick={() => onSelect?.(index)}
                  className={classNames('border-b border-gray-100',
                    selected === index ? 'bg-blue-50' : mark === 'error' ? 'bg-red-50' : mark === 'warn' ? 'bg-amber-50' : 'hover:bg-gray-50',
                    onSelect && 'cursor-pointer')}>
                  <td className={classNames('px-1 text-center', mark === 'error' ? 'text-red-600' : mark === 'warn' ? 'text-amber-600' : 'text-gray-400')}>{ri + 1}</td>
                  {cols.map((c, ci) => (
                    <td key={c.key} className="px-0.5 py-0.5">
                      {c.options ? (
                        <select className="w-full border border-transparent hover:border-gray-300 focus:border-blue-400 rounded px-1 py-0.5 bg-transparent"
                          value={String(row[c.key] ?? '')} disabled={readOnly} onChange={(e) => onChange(index, { [c.key]: e.target.value } as Partial<T>)}>
                          {!c.options.some((o) => o.value === String(row[c.key] ?? '')) && <option value={String(row[c.key] ?? '')}>{String(row[c.key] ?? '')}</option>}
                          {c.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      ) : (
                        <input className={classNames('w-full border border-transparent hover:border-gray-300 focus:border-blue-400 rounded px-1 py-0.5 bg-transparent', c.mono && 'font-mono')}
                          value={String(row[c.key] ?? '')} readOnly={readOnly} list={c.list ? `${id}-${c.key}-list` : undefined}
                          onChange={(e) => onChange(index, { [c.key]: e.target.value } as Partial<T>)}
                          onPaste={(e) => !readOnly && paste(e, ri, ci)} />
                      )}
                    </td>
                  ))}
                  {extra && <td className="px-1 text-gray-500 whitespace-nowrap">{extra.render(row)}</td>}
                  <td className="px-1 text-center">
                    {!readOnly && (
                      <button type="button" className="text-gray-300 hover:text-red-600" title={t('mapping:grid.removeRow')}
                        onClick={(e) => { e.stopPropagation(); onRemove(index); }}>✕</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!rows.length && <div className="p-4 text-center text-xs text-gray-400">{empty}</div>}
        {cols.filter((c) => c.list).map((c) => (
          <datalist key={c.key} id={`${id}-${c.key}-list`}>{c.list!.map((v) => <option key={v} value={v} />)}</datalist>
        ))}
      </div>
      {!readOnly && (
        <div className="pt-1">
          <button type="button" className="text-xs text-blue-600 hover:underline" onClick={onAdd}>+ {t('mapping:grid.addRow')}</button>
          <span className="ml-3 text-[0.6875rem] text-gray-400">{t('mapping:grid.pasteHint')}</span>
        </div>
      )}
    </div>
  );
}

export default SpecGrid;
